import {
	SET_AUTOPLAY,
	SET_AUTOSTART_COUCH_MODE,
	SET_IS_EMBEDDED,
	SET_LOGO_ENABLED,
	SET_PLAYBACK_SPEED_ENABLED,
	SET_QUALITY_SETTINGS_ENABLED,
	SET_PLAYER_CONTENT_INFO_ENABLED,
	SET_PLAYER_FORCE_DISABLE_CHROMECAST,
	SET_PLAYER_LIVE_BADGE_ENABLED,
	SET_PLAYER_NATIVE_FULLSCREEN_ON_IOS,
	SET_PLAYER_PAUSE_ENABLED,
	SET_PLAYER_SEEK_ENABLED,
	SET_PLAYER_STOP_ENABLED,
	SET_PLAYER_TIME_ENABLED,
	SET_SHARING_ENABLED,
	SET_STATE,
	SET_TIMELINE_ENABLED,
	SET_TIMELINE_MINIMAL,
	SET_TIMELINE_SHOW_TAGS,
	SET_DEBUG,
	SET_FORCE_OVERLAY_VISIBLE,
	SET_LOOP_BACKGROUND_MODE,
	SET_DISABLE_PROFILE,
	SET_DISABLE_ADS,
} from '../../constants/mutations';

/*
 Only use this module to enable/disable features in the player.
 Do not use this module to store the state of the player.
 */
export default {
	namespaced: true,
	state:      () => ({
		isEmbedded: false,
		features:   {
			autostartCouchMode: false,
			timeline:           {
				enable:   true,
				minimal:  false,
				showTags: [],
			},
			player: {
				autoplay:               true,
				time:                   true,
				liveBadge:              true,
				logo:                   true,
				playbackSpeedEnabled:   true,
				qualitySettingsEnabled: true,
				seek:                   true,
				pause:                  true,
				stop:                   false,
				contentInfoEnabled:     true,
				forceDisableChromeCast: false,
				nativeFullscreenOnIos:  false,
				cinemaMode:             false,
				previewMode:            false,
				initialPreviewDuration: null,
				forceOverlayVisible:    false,
			},
			sharing:              true,
			loopedBackgroundMode: false,
			disableProfile:       false,
			disableAds:           false,
			debug:                {
				bus:    false,
				player: false,
			},
		},
	}),
	mutations: {
		[SET_STATE]: (state, payload) => {
			for (const [k, v] of Object.entries(payload)) {
				state[k] = v;
			}
		},
		[SET_IS_EMBEDDED]: (state, value) => {
			state.isEmbedded = value;
		},
		// init
		[SET_AUTOSTART_COUCH_MODE]: (state, value) => {
			state.features.autostartCouchMode = value;
		},
		// timeline features
		[SET_TIMELINE_ENABLED]: (state, value) => {
			state.features.timeline.enable = value;
		},
		[SET_TIMELINE_MINIMAL]: (state, value) => {
			state.features.timeline.minimal = value;
		},
		[SET_TIMELINE_SHOW_TAGS]: (state, value) => {
			state.features.timeline.showTags = value;
		},
		// player features
		[SET_AUTOPLAY]: (state, value) => {
			state.features.player.autoplay = value;
		},
		[SET_LOGO_ENABLED]: (state, value) => {
			state.features.player.logo = value;
		},
		[SET_PLAYBACK_SPEED_ENABLED]: (state, value) => {
			state.features.player.playbackSpeedEnabled = value;
		},
		[SET_QUALITY_SETTINGS_ENABLED]: (state, value) => {
			state.features.player.qualitySettingsEnabled = value;
		},
		[SET_PLAYER_TIME_ENABLED]: (state, value) => {
			state.features.player.time = value;
		},
		[SET_PLAYER_SEEK_ENABLED]: (state, value) => {
			state.features.player.seek = value;
		},
		[SET_PLAYER_PAUSE_ENABLED]: (state, value) => {
			state.features.player.pause = value;
		},
		[SET_PLAYER_STOP_ENABLED]: (state, value) => {
			state.features.player.stop = value;
		},
		[SET_PLAYER_LIVE_BADGE_ENABLED]: (state, value) => {
			state.features.player.liveBadge = value;
		},
		[SET_SHARING_ENABLED]: (state, value) => {
			state.features.sharing = value;
		},
		[SET_PLAYER_CONTENT_INFO_ENABLED]: (state, value) => {
			state.features.player.contentInfoEnabled = Boolean(value);
		},
		[SET_PLAYER_FORCE_DISABLE_CHROMECAST]: (state, value) => {
			state.features.player.forceDisableChromeCast = Boolean(value);
		},
		[SET_PLAYER_NATIVE_FULLSCREEN_ON_IOS]: (state, value) => {
			state.features.player.nativeFullscreenOnIos = Boolean(value);
		},
		[SET_DEBUG]: (state, { key, value }) => {
			if (!(key in state.features.debug)) throw new Error(`Unknown debug flag: ${key}`);
			state.features.debug[key] = value;
		},
		[SET_FORCE_OVERLAY_VISIBLE]: (state, value) => {
			state.features.player.forceOverlayVisible = value;
		},
		[SET_LOOP_BACKGROUND_MODE]: (state, value) => {
			state.features.loopedBackgroundMode = value;
		},
		[SET_DISABLE_PROFILE]: (state, value) => {
			state.features.disableProfile = value;
		},
		[SET_DISABLE_ADS]: (state, value) => {
			state.features.disableAds = value;
		},
	},
	actions: {
		setState:                  ({ commit }, value) => commit(SET_STATE, value),
		setEmbedded:               ({ commit }, value = true) => commit(SET_IS_EMBEDDED, value),
		enableTimeline:            ({ commit }) => commit(SET_TIMELINE_ENABLED, true),
		disableTimeline:           ({ commit }) => commit(SET_TIMELINE_ENABLED, false),
		setTimelineMinimal:        ({ commit }) => commit(SET_TIMELINE_MINIMAL, true),
		setLogoEnabled:            ({ commit }, value) => commit(SET_LOGO_ENABLED, value),
		setPlaybackSpeedEnabled:   ({ commit }, value) => commit(SET_PLAYBACK_SPEED_ENABLED, value),
		setQualitySettingsEnabled: ({ commit }, value) => commit(SET_QUALITY_SETTINGS_ENABLED, value),
		enableAutoplay:            ({ commit }) => commit(SET_AUTOPLAY, true),
		disableAutoplay:           ({ commit }) => commit(SET_AUTOPLAY, false),
		enableAutostartCouchMode:  ({ commit }) => commit(SET_AUTOSTART_COUCH_MODE, true),
		disableAutostartCouchMode: ({ commit }) => commit(SET_AUTOSTART_COUCH_MODE, false),
		setDebug:                  ({ commit }, { key, value }) => commit(SET_DEBUG, { key, value }),
		setForceOverlayVisible:    ({ commit }, value) => commit(SET_FORCE_OVERLAY_VISIBLE, value),
		setForceDisableChromeCast: ({ commit }, value) => commit(SET_PLAYER_FORCE_DISABLE_CHROMECAST, value),
		setNativeFullscreenOnIos:  ({ commit }, value) => commit(SET_PLAYER_NATIVE_FULLSCREEN_ON_IOS, value),
		setPlayerSeekEnabled:      ({ commit }, value) => commit(SET_PLAYER_SEEK_ENABLED, value),
		setLoopBackgroundMode:     ({ commit }, value) => commit(SET_LOOP_BACKGROUND_MODE, value),
		setDisableProfile:         ({ commit }, value) => commit(SET_DISABLE_PROFILE, value),
		setDisableAds:             ({ commit }, value) => commit(SET_DISABLE_ADS, value),
	},
	getters: {
		debug:                      (state) => state.features.debug,
		features:                   (state) => state.features,
		forceMinimalTimeline:       (state) => state.features.timeline.minimal,
		showTags:                   (state) => state.features.timeline.showTags,
		isLogoEnabled:              (state) => state.features.player.logo,
		isPlaybackSpeedEnabled:     (state) => state.features.player.playbackSpeedEnabled,
		isQualitySettingsEnabled:   (state) => state.features.player.qualitySettingsEnabled,
		autoplay:                   (state) => state.features.player.autoplay || state.features.loopedBackgroundMode,
		isPlayerTimeEnabled:        (state) => state.features.player.time,
		isLiveBadgeEnabled:         (state) => state.features.player.liveBadge,
		isSeekEnabled:              (state) => state.features.player.seek,
		isPauseEnabled:             (state) => state.features.player.pause,
		isStopEnabled:              (state) => state.features.player.stop,
		isSharingEnabled:           (state) => state.features.sharing,
		isPlayerContentInfoEnabled: (state) => state.features.player.contentInfoEnabled,
		forceDisableChromeCast:     (state) => state.features.player.forceDisableChromeCast,
		nativeFullscreenOnIos:      (state) => state.features.player.nativeFullscreenOnIos,
		autostartCouchMode:         (state) => state.features.autostartCouchMode,
		forceOverlayVisible:        (state) => state.features.player.forceOverlayVisible || window.location.search.includes('forceOverlayVisible'),
		isTimelineEnabled:          (state) => state.features.timeline.enable,
		isEmbedded:                 (state) => state.isEmbedded,
		isLoopBackgroundMode:       (state) => state.features.loopedBackgroundMode,
		forceMuted:                 (state) => state.features.loopedBackgroundMode,
		loop:                       (state) => state.features.loopedBackgroundMode,
		clickInteractionEnabled:    (state) => !state.features.loopedBackgroundMode,
		keyInteractionEnabled:      (state) => !state.features.loopedBackgroundMode,
		isProfileDisabled:          (state) => state.features.disableProfile,
		isAdsDisabled:              (state) => state.features.disableAds,
		hasMinimalTimeline:         (state, getters, rootState, rootGetters) => !rootGetters['app/isTaggingToolMode'] && (getters.forceMinimalTimeline || !rootGetters['event/timelineTags'].length),
	},
};
